import { ColumnBlockModel, ColumnsBlockModel } from '@blocksuite/affine-model';
import { matchModels } from '@blocksuite/affine-shared/utils';
import { ShadowlessElement } from '@blocksuite/std';
import { html } from 'lit';
import { property } from 'lit/decorators.js';

import type { ColumnBlockComponent } from './column-block.js';
import {
  columnDividerLineStyles,
  columnDividerStyles,
  MIN_COLUMN_RATIO,
} from './styles.js';

type DragState = {
  pointerId: number;
  startX: number;
  prevEl: HTMLElement;
  nextEl: HTMLElement;
  prevModel: ColumnBlockModel;
  nextModel: ColumnBlockModel;
  /** Combined px width of the two columns being resized. */
  pairPx: number;
  /** Combined flex ratio of the same two columns. */
  pairRatio: number;
  /** Smallest ratio either column may take, derived from the whole row. */
  minRatio: number;
  prevPx: number;
};

/**
 * Grab strip sitting in the gutter to the left of its column. Dragging it
 * transfers flex ratio between that column and the one before it. Ratios are
 * written to the model once, on pointer-up, so a resize is a single undo step.
 */
export class ColumnDividerComponent extends ShadowlessElement {
  private _drag: DragState | null = null;

  @property({ attribute: false })
  accessor column!: ColumnBlockComponent;

  private readonly _onPointerDown = (event: PointerEvent) => {
    if (event.button !== 0) return;

    const state = this._captureDragState(event);
    if (!state) return;

    // Keeps the drag from starting a text selection or moving the caret.
    event.preventDefault();
    event.stopPropagation();

    this._drag = state;
    this.dataset.dragging = 'true';
    this.setPointerCapture(event.pointerId);
  };

  private readonly _onPointerMove = (event: PointerEvent) => {
    const drag = this._drag;
    if (!drag || event.pointerId !== drag.pointerId) return;

    const { prevRatio, nextRatio } = this._ratiosAt(drag, event.clientX);
    drag.prevEl.style.flex = `${prevRatio} 1 0`;
    drag.nextEl.style.flex = `${nextRatio} 1 0`;
  };

  private readonly _onPointerUp = (event: PointerEvent) => {
    const drag = this._drag;
    if (!drag || event.pointerId !== drag.pointerId) return;

    this._drag = null;
    delete this.dataset.dragging;
    this.releasePointerCapture(event.pointerId);

    const { prevRatio, nextRatio } = this._ratiosAt(drag, event.clientX);
    const store = drag.prevModel.store;
    if (store.readonly) return;

    store.transact(() => {
      drag.prevModel.props.width = prevRatio;
      drag.nextModel.props.width = nextRatio;
    });
  };

  private _captureDragState(event: PointerEvent): DragState | null {
    const nextModel = this.column?.model;
    const row = nextModel?.parent;
    if (!nextModel || !row || !matchModels(row, [ColumnsBlockModel])) {
      return null;
    }
    if (nextModel.store.readonly) return null;

    const index = row.children.indexOf(nextModel);
    const prevModel = row.children[index - 1];
    if (!prevModel || !matchModels(prevModel, [ColumnBlockModel])) return null;

    const prevEl = this.column.std.view.getBlock(prevModel.id);
    if (!prevEl) return null;

    const prevPx = prevEl.getBoundingClientRect().width;
    const nextPx = this.column.getBoundingClientRect().width;
    const pairPx = prevPx + nextPx;
    if (pairPx <= 0) return null;

    const prevRatio = prevModel.props.width;
    const nextRatio = nextModel.props.width;
    const pairRatio = prevRatio + nextRatio;

    const rowRatio = row.children.reduce(
      (sum, child) =>
        sum + (matchModels(child, [ColumnBlockModel]) ? child.props.width : 0),
      0
    );
    const minRatio = rowRatio * MIN_COLUMN_RATIO;
    // Two minimums have to fit inside the pair, or there is nothing to drag.
    if (pairRatio <= minRatio * 2) return null;

    return {
      pointerId: event.pointerId,
      startX: event.clientX,
      prevEl,
      nextEl: this.column,
      prevModel,
      nextModel,
      pairPx,
      pairRatio,
      minRatio,
      prevPx,
    };
  }

  /** Maps a pointer x back onto a ratio split of the dragged pair. */
  private _ratiosAt(drag: DragState, clientX: number) {
    const targetPx = drag.prevPx + (clientX - drag.startX);
    const raw = drag.pairRatio * (targetPx / drag.pairPx);
    const prevRatio = Math.min(
      Math.max(raw, drag.minRatio),
      drag.pairRatio - drag.minRatio
    );
    return { prevRatio, nextRatio: drag.pairRatio - prevRatio };
  }

  override connectedCallback() {
    super.connectedCallback();
    this.classList.add('affine-column-divider', columnDividerStyles);
    this.addEventListener('pointerdown', this._onPointerDown);
    this.addEventListener('pointermove', this._onPointerMove);
    this.addEventListener('pointerup', this._onPointerUp);
    this.addEventListener('pointercancel', this._onPointerUp);
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('pointerdown', this._onPointerDown);
    this.removeEventListener('pointermove', this._onPointerMove);
    this.removeEventListener('pointerup', this._onPointerUp);
    this.removeEventListener('pointercancel', this._onPointerUp);
  }

  override render() {
    return html`<div class="${columnDividerLineStyles}"></div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'affine-column-divider': ColumnDividerComponent;
  }
}
